import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import i18next from 'i18next';
import _ from 'lodash';
import React, { FC, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { PutClient } from '../../clients';
import { useHrpsApi } from '../../clients/useHrpsApi';
import { WaitFor } from '../../components/blocks';
import { Button, Checkbox, Text, TranslatedString, useTranslation } from '../../components/blocks/localization';
import { CreateReturnOrderInfoBox, Tile, usePageMessaging } from '../../components/composites';
import {
    BulkActionTile,
    checkboxFormat,
    createReturnOrderPageLabelSelectFormat,
    HelpTablePopover,
    imagePopupFormat,
    MultilineFormEditor,
    MultilineFormEditorColumn,
    MultilineFormEditorColumnFormat,
    numberFormat,
    searchableSelectFormat,
    selectFormat,
    textFormat,
    uploadImageFormat,
} from '../../components/constructed';
import { createReturnOrderPageReturnReasonFormat } from '../../components/constructed/MultilineFormEditor/formats/createReturnOrderPageReturnReasonFormat';
import { COMPLIANCE_RESOLUTION, RETURN_REASON } from '../../constants';
import { customerRefRegEx } from '../../constants/customerReference';
import { RETURN_TYPE } from '../../constants/returnType';
import { useReturnReasons } from '../../hooks/useReturnReasons';
import { fetchAttachmentsByReturnId } from '../../redux/actions/attachmentsActions';
import { getResellerDetails, getResellerItems, getResellerPriceList } from '../../redux/actions/resellerActionCreators';
import {
    clearReturnOrderDraft,
    getReturnOrderDraft,
    setReturnOrderDraft,
} from '../../redux/actions/returnOrderDraftActionCreators';
import { clearReturnOrderList } from '../../redux/actions/returnOrderListActionCreators';
import { clearAttachments, setAttachments, setStatus } from '../../redux/reducers/attachmentsReducer';
import {
    ASIN_MISSING_PLACEHOLDER,
    ComplianceCalculationStatus,
    CreateReturnOrderLineItem,
    DEFAULT_ITEM_IMAGE_URL,
    IAsynchronouslyLoadedValue,
    ResellerItem,
    ReturnOrderDraft,
    ReturnOrderType,
    RootState,
} from '../../redux/types';
import {
    Attachment,
    CreateReturnOrderDraftLine,
    DeleteAttachmentRequest,
    DeleteAttachmentsInput,
    PackageType,
    packageTypes,
    RequestAttachmentUploadUrlInput,
    RequestAttachmentUploadUrlOutput,
    SiteMapPage,
} from '../../types';

import { Logger } from '../../utils/logger';
import { getReturnReason, isNormalizedReturnReason, normalizeReturnReason } from '../../utils/returnReasons';
import { useSiteMapRouter } from '../../utils/SiteMapRouter';
import { useComplianceCalculator } from './useComplianceCalculator';
import { useCreateReturnOrderFormValidator } from './useCreateReturnOrderFormValidator';

export type CreateReturnOrderFormErrors = {
    referenceId?: TranslatedString;
    shippingAddress?: TranslatedString;
    billingAddress?: TranslatedString;
    contactEmails?: TranslatedString;
    orderLines?: TranslatedString;
    returnOrderType?: TranslatedString;
};

export { customerRefRegEx };

// FIXME: This function is way too big - refactor to make it smaller. -- 4/15/22
// eslint-disable-next-line max-lines-per-function
const CreateReturnOrderPage: FC<CreateReturnOrderPageProps> = ({
    asinToImageUrls,
    resellerId,
    resellerDetails,
    resellerItems,
    resellerPriceList,
    returnOrderDraft,
    getResellerDetails,
    getResellerItems,
    getResellerPriceList,
    setReturnOrderDraft,
    getReturnOrderDraft,
    clearReturnOrderDraft,
    clearReturnOrderList,
    attachments,
    fetchAttachmentsByReturnId,
    clearAttachments,
    setAttachmentsStatus,
    setAttachments,
    zcrEnabled,
    resellerType,
    adminUser,
    userEmail,
}) => {
    const { t } = useTranslation(['createReturnOrderPage']);
    const { goto } = useSiteMapRouter();

    useEffect(() => {
        if (resellerType === 'Indirect Retailer') {
            goto(SiteMapPage.listReturnOrders);
        }
    }, [resellerType, goto]);

    const [isPageBusy, setIsPageBusy] = useState(false);
    const [isComplianceMeasured, setIsComplianceMeasured] = useState(false);
    const [cleared, setCleared] = useState(false);
    const [showFormErrors, setShowFormErrors] = useState<boolean>(false);
    const [hasErrorBeenReportedReturnOrderDraft, setHasErrorBeenReportedReturnOrderDraft] = useState<boolean>(false);
    const [hasErrorBeenReportedResellerDetails, setHasErrorBeenReportedResellerDetails] = useState<boolean>(false);
    const [hasErrorBeenReportedResellerItems, setHasErrorBeenReportedResellerItems] = useState<boolean>(false);
    const [hasErrorBeenReportedResellerPriceList, setHasErrorBeenReportedResellerPriceList] = useState<boolean>(false);
    const { returnOrderId } = useParams<{ returnOrderId: string }>();
    const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);
    const [itemsNeedingActionNumber, setItemsNeedingActionNumber] = useState<number>(0);
    const [selectedItemsNumber, setSelectedItemsNumber] = useState<number>(0);
    const idRef = useRef<number>(0);
    const adminRoles = ['amazonAdmin'];
    const isAmazonAdminUser = adminUser !== undefined && _.intersection(adminUser.roles, adminRoles).length > 0;
    const returnReasons = useReturnReasons();

    const {
        returnOrders: {
            createReturnOrderDraft,
            deleteAttachments,
            updateReturnOrderDraft,
            getReturnOrderDocuments,
            deleteReturnOrderDraft,
            requestAttachmentUploadUrl,
        },
    } = useHrpsApi();

    useEffect(() => {
        if (attachments.status === 'Uninitialized') {
            if (!returnOrderId) {
                setAttachmentsStatus('Loaded');
            } else {
                fetchAttachmentsByReturnId({ returnOrderId });
            }
        }
    }, [
        setAttachments,
        returnOrderId,
        getReturnOrderDocuments,
        setAttachmentsStatus,
        attachments,
        fetchAttachmentsByReturnId,
    ]);

    const { showSuccess, showError } = usePageMessaging();

    const zcrComplianceActions = zcrEnabled
        ? [{ value: COMPLIANCE_RESOLUTION.DISPOSE, label: t('dispose-labelText') }]
        : [];
    const complianceActions = [
        { value: COMPLIANCE_RESOLUTION.ACCEPT, label: t('accept-labelText') },
        { value: COMPLIANCE_RESOLUTION.TRIM, label: t('trim-labelText') },
        ...zcrComplianceActions,
    ];

    const {
        getValidLineItems,
        buildComplianceCalculationInputLines,
        calculateOrderLinesCompliance,
    } = useComplianceCalculator();

    const supportedReturnTypes = [RETURN_TYPE.RETURN, RETURN_TYPE.BUYBACK, RETURN_TYPE.REFUSAL];

    const zcrComplianceHelpItems = zcrEnabled
        ? [
              {
                  type: t('dispose-labelText'),
                  description: t('contractResolutionHelpDetails_DISPOSE'),
              },
          ]
        : [];

    const complianceHelpItems = [
        {
            type: t('trim-labelText'),
            description: t('contractResolutionHelpDetails_TRIM'),
        },
        {
            type: t('accept-labelText'),
            description: t('contractResolutionHelpDetails_ACCEPT'),
        },
        ...zcrComplianceHelpItems,
    ];

    const { formIsValid, formErrors } = useCreateReturnOrderFormValidator(
        {
            referenceId: returnOrderDraft.value?.referenceId,
            shippingAddress: returnOrderDraft.value?.shippingLocationCode,
            billingAddress: returnOrderDraft.value?.billingLocationCode,
            contactEmails: returnOrderDraft.value?.contactEmailAddresses,
            orderLines: returnOrderDraft.value
                ? returnOrderDraft.value.orderLines.filter((item) => item.externalSku)
                : [],
            returnOrderType: returnOrderDraft.value?.returnType,
        },
        resellerItems.value
    );

    const showErrors = useCallback(() => {
        if (showFormErrors) {
            return formErrors;
        } else {
            return {};
        }
    }, [formErrors, showFormErrors]);

    useEffect(() => {
        if (!cleared) {
            clearReturnOrderDraft();
            clearAttachments();
            setCleared(true);
        }
    }, [clearReturnOrderDraft, cleared, clearAttachments]);

    const setComplianceCalculationStatus = useCallback(
        async (
            returnOrder: ReturnOrderDraft,
            status: ComplianceCalculationStatus,
            validLineItems: CreateReturnOrderLineItem[]
        ) => {
            if (returnOrder) {
                const tempReturnOrder = {
                    ...returnOrder,
                    orderLines: returnOrder?.orderLines.map((item) =>
                        validLineItems.some((validItem) => validItem.orderLineId === item.orderLineId)
                            ? { ...item, complianceCalculationStatus: status }
                            : item
                    ),
                };
                setReturnOrderDraft(tempReturnOrder);
            }
        },
        [setReturnOrderDraft]
    );

    const externalSkuToItemMap = useMemo(() => {
        return new Map(resellerItems.value?.map((item) => [item.externalSku, item]));
    }, [resellerItems.value]);

    const returnOrderValid = useCallback((returnOrder: ReturnOrderDraft): boolean => {
        const invalidLineItems = returnOrder.orderLines.filter(
            (item) =>
                item.externalSku &&
                ((returnOrder.returnType === RETURN_TYPE.RETURN &&
                    (item.complianceCalculationStatus !== 'Complete' ||
                        item.quantity === 0 ||
                        (item.complianceResult !== undefined &&
                            (item.complianceResolution === undefined ||
                                item.complianceResolution === COMPLIANCE_RESOLUTION.NONE)))) ||
                    (returnOrder.returnType !== RETURN_TYPE.RETURN && item.quantity === 0))
        );
        const validReferenceId =
            returnOrder.referenceId !== '' &&
            returnOrder.referenceId.length > 0 &&
            returnOrder.referenceId.length < 31 &&
            customerRefRegEx.test(returnOrder.referenceId);
        return validReferenceId && returnOrder.orderLines.length > 1 && invalidLineItems.length === 0;
    }, []);

    const calculateCompliance = useCallback(
        async (returnOrderDraft: IAsynchronouslyLoadedValue<ReturnOrderDraft>) => {
            if (returnOrderDraft.value) {
                const validLineItems = getValidLineItems(returnOrderDraft.value?.orderLines);
                setIsSubmitEnabled(false);

                if (validLineItems.length <= 0) {
                    return;
                }

                const { calculateComplianceLineItems, invalidSkus } = buildComplianceCalculationInputLines(
                    validLineItems,
                    resellerItems.value!
                );

                if (invalidSkus.length) {
                    Logger.debug(t('skuNotFound-errorMessage', { skus: invalidSkus.join(', ') }));
                    showError(t('skuNotFound-errorMessage', { skus: invalidSkus.join(', ') }));
                    return;
                }

                if (returnOrderDraft.value && resellerPriceList.value && returnOrderDraft.value.returnType) {
                    setComplianceCalculationStatus(returnOrderDraft.value, 'Loading', validLineItems);
                    try {
                        const complianceOutput = await calculateOrderLinesCompliance(
                            resellerPriceList.value.priceListItems,
                            resellerId,
                            externalSkuToItemMap,
                            validLineItems,
                            calculateComplianceLineItems,
                            returnOrderDraft.value.returnType
                        );
                        const lineItems = sortedOrderLines(
                            returnOrderDraft.value.orderLines.map((line) =>
                                complianceOutput[line.orderLineId] ? complianceOutput[line.orderLineId] : line
                            )
                        );
                        const tempReturnOrder = {
                            ...returnOrderDraft.value,
                            orderLines: lineItems,
                        };
                        setReturnOrderDraft(tempReturnOrder);
                        await setComplianceCalculationStatus(tempReturnOrder, 'Complete', validLineItems);
                        setIsSubmitEnabled(returnOrderValid(tempReturnOrder));
                    } catch (e) {
                        Logger.debug(`${t('complianceCalculationFailed-errorMessage')} ${e}`);
                        showError(t('complianceCalculationFailed-errorMessage'));
                        await setComplianceCalculationStatus(returnOrderDraft.value, 'Failed', validLineItems);
                    }
                }
            }
        },
        [
            buildComplianceCalculationInputLines,
            calculateOrderLinesCompliance,
            externalSkuToItemMap,
            getValidLineItems,
            resellerId,
            resellerItems.value,
            resellerPriceList.value,
            returnOrderValid,
            setComplianceCalculationStatus,
            setReturnOrderDraft,
            showError,
            t,
        ]
    );

    useEffect(() => {
        switch (returnOrderDraft.status) {
            case 'Uninitialized':
                setHasErrorBeenReportedReturnOrderDraft(false);
                if (returnOrderId) {
                    getReturnOrderDraft(returnOrderId, resellerId);
                } else if (resellerDetails.value) {
                    let emails = resellerDetails.value.contacts
                        .filter((item) => item.contactType === 'Returns' || item.contactType === 'Credit Memos')
                        .map((item) => item.email);
                    if (userEmail && !emails.includes(userEmail)) {
                        emails = [...emails, userEmail];
                    }
                    const initialReturnOrderDraft: ReturnOrderDraft = {
                        referenceId: '',
                        contactEmailAddresses: emails,
                        returnType: '',
                        shippingLocationCode: resellerDetails.value.resellerLocations
                            .primaryShippingAddressLocationCode!,
                        billingLocationCode: resellerDetails.value.resellerLocations.primaryBillingAddressLocationCode!,
                        orderLines: [buildNewItem()],
                    };
                    setReturnOrderDraft(initialReturnOrderDraft);
                }
                break;
            case 'Failed':
                Logger.debug(t('switch (returnOrderDraft.status)Failed:failedToLoadReturnOrderDraft-errorMessage'));
                if (!hasErrorBeenReportedReturnOrderDraft) {
                    showError(t('failedToLoadReturnOrderDraft-errorMessage'));
                    setHasErrorBeenReportedReturnOrderDraft(true);
                }
                break;
            case 'Loaded':
                if (returnOrderDraft.value && resellerItems.value) {
                    if (returnOrderDraft.value.orderLines.find((item) => item.itemId && !item.externalSku)) {
                        // fresh load of data reload the external skus
                        const orderLines = returnOrderDraft.value.orderLines.map((item) => {
                            const matchedItem = resellerItems.value?.find((i) => i.itemId === item.itemId);

                            // Transform custom reasons
                            const isCustomReason = item.returnReason && !isNormalizedReturnReason(item.returnReason);
                            const transformedItem = isCustomReason
                                ? { ...item, customReason: item.returnReason, returnReason: RETURN_REASON.OTHER }
                                : {
                                      ...item,
                                      returnReason: normalizeReturnReason(item.returnReason),
                                  };

                            if (matchedItem) {
                                return {
                                    ...transformedItem,
                                    externalSku: matchedItem.externalSku,
                                    asin: matchedItem.asin,
                                    itemId: matchedItem.itemId,
                                    title: matchedItem.title,
                                };
                            } else {
                                return {
                                    ...transformedItem,
                                    externalSku: 'Unknown',
                                    asin: 'Unknown',
                                    itemId: 'Unknown',
                                    title: 'Unknown',
                                };
                            }
                        });
                        // load the parent Id when a item has a COMPLIANCE_RESOLUTION other than none.
                        const sortedLines = orderLines.sort(
                            (firstItem: CreateReturnOrderLineItem, secondItem: CreateReturnOrderLineItem) => {
                                const firstItemString = `${firstItem.externalSku}-${
                                    firstItem.complianceResolution === undefined ||
                                    firstItem.complianceResolution === COMPLIANCE_RESOLUTION.NONE
                                        ? ' '
                                        : firstItem.complianceResolution
                                }`;
                                const secondItemString = `${secondItem.externalSku}-${
                                    secondItem.complianceResolution === undefined ||
                                    secondItem.complianceResolution === COMPLIANCE_RESOLUTION.NONE
                                        ? ' '
                                        : secondItem.complianceResolution
                                }`;
                                if (firstItemString > secondItemString) {
                                    return 1;
                                } else if (firstItemString < secondItemString) {
                                    return -1;
                                }
                                return 0;
                            }
                        );
                        const finalOrderLines = sortedLines.map((item, index) => {
                            // Reset the orderLineId based on index position and
                            // associate to previous line item where we should
                            return {
                                ...item,
                                orderLineId: index,
                                parentOrderLineId:
                                    item.complianceResolution !== COMPLIANCE_RESOLUTION.NONE &&
                                    item.itemId === sortedLines[index - 1]?.itemId
                                        ? index - 1
                                        : undefined,
                            };
                        });
                        const orderLineIds = finalOrderLines.map((item) => item.orderLineId);
                        idRef.current = Math.max(...orderLineIds) + 1;
                        const tempReturnOrder = {
                            ...returnOrderDraft.value,
                            orderLines: [...finalOrderLines, buildNewItem()],
                        };
                        setReturnOrderDraft(tempReturnOrder);
                        setIsSubmitEnabled(returnOrderValid(tempReturnOrder));
                    }
                }
                break;
            default:
            // do nothing
        }
    }, [
        calculateCompliance,
        getReturnOrderDraft,
        hasErrorBeenReportedReturnOrderDraft,
        resellerDetails.value,
        resellerId,
        resellerItems.value,
        returnOrderDraft,
        returnOrderDraft.error,
        returnOrderDraft.status,
        returnOrderDraft.value,
        returnOrderId,
        returnOrderValid,
        setReturnOrderDraft,
        showError,
        t,
        userEmail,
    ]);

    // TODO: Generalize handling for non-loaded redux state - see https://sim.amazon.com/issues/Bender-1651
    useEffect(() => {
        if (resellerId) {
            switch (resellerDetails.status) {
                case 'Uninitialized':
                    getResellerDetails(resellerId);
                    setHasErrorBeenReportedResellerDetails(false);
                    break;
                case 'Failed':
                    Logger.debug(t('resellerDetails.status:Failed:failedToLoadReturnOrderDraft-errorMessage'));
                    if (!hasErrorBeenReportedResellerDetails) {
                        showError(t('failedToLoadResellerDetails-errorMessage'));
                        setHasErrorBeenReportedResellerDetails(true);
                    }
                    break;
                default:
                // do Nothing
            }
        }
    }, [
        getResellerDetails,
        hasErrorBeenReportedResellerDetails,
        resellerDetails,
        resellerId,
        returnOrderId,
        setReturnOrderDraft,
        showError,
        t,
    ]);

    useEffect(() => {
        if (resellerId) {
            switch (resellerPriceList.status) {
                case 'Uninitialized':
                    getResellerPriceList(resellerId);
                    setHasErrorBeenReportedResellerItems(false);
                    break;
                case 'Failed':
                    Logger.error('getResellerPriceList error: ', resellerPriceList.error);
                    if (!hasErrorBeenReportedResellerPriceList) {
                        showError(t('selectResellerPage:failedToLoadResellerPriceList-errorMessage'));
                        setHasErrorBeenReportedResellerPriceList(true);
                    }
                    break;
                default:
                // do nothing
            }
        }
    }, [resellerPriceList, resellerId, getResellerPriceList, showError, t, hasErrorBeenReportedResellerPriceList]);

    useEffect(() => {
        if (resellerId) {
            switch (resellerItems.status) {
                case 'Uninitialized':
                    getResellerItems(resellerId);
                    setHasErrorBeenReportedResellerItems(false);
                    break;
                case 'Failed':
                    Logger.debug(t('useEffect:resellerItems.status:failedToLoadReturnOrderDraft-errorMessage'));
                    if (!hasErrorBeenReportedResellerItems) {
                        showError(t('failedToLoadResellerItems-errorMessage'));
                        setHasErrorBeenReportedResellerItems(true);
                    }
                    break;
                default:
                // do Nothing
            }
        }
    }, [resellerItems, resellerId, getResellerItems, showError, t, hasErrorBeenReportedResellerItems]);

    const sortedOrderLines = (orderLines: CreateReturnOrderLineItem[]): CreateReturnOrderLineItem[] => {
        return orderLines.sort((firstItem: CreateReturnOrderLineItem, secondItem: CreateReturnOrderLineItem) => {
            if (firstItem.orderLineId > secondItem.orderLineId) {
                return 1;
            } else if (firstItem.orderLineId < secondItem.orderLineId) {
                return -1;
            }
            return 0;
        });
    };

    const externalSkuOptions = useMemo(() => {
        return resellerItems.value
            ? resellerItems.value
                  .filter((item) => item.externalSku && item.upc && item.asin && item.itemId)
                  .map((item) => {
                      return { value: item.externalSku, label: t.cognate(item.externalSku) };
                  })
            : [];
    }, [resellerItems.value, t]);

    const upcOptions = useMemo(() => {
        return resellerItems.value
            ? resellerItems.value
                  .filter((item) => item.externalSku && item.upc && item.asin && item.itemId)
                  .map((item) => {
                      return { value: item.externalSku, label: t.cognate(item.upc) };
                  })
            : [];
    }, [resellerItems.value, t]);

    const asinOptions = useMemo(() => {
        return resellerItems.value
            ? resellerItems.value
                  .filter((item) => item.externalSku && item.upc && item.asin && item.itemId)
                  .map((item) => {
                      return { value: item.externalSku, label: t.cognate(item.asin) };
                  })
            : [];
    }, [resellerItems.value, t]);

    const itemOptions = useMemo(() => {
        return resellerItems.value
            ? resellerItems.value
                  .filter((item) => item.externalSku && item.upc && item.asin && item.itemId)
                  .map((item) => {
                      return { value: item.externalSku, label: t.cognate(item.title) };
                  })
            : [];
    }, [resellerItems.value, t]);

    const getItemImageUrlAndDataFromStore = useCallback(
        (sku: string): { url: string; title: string } | undefined => {
            if (!resellerItems.value) return { url: t('loading'), title: t('loading') };
            const asin = resellerItems.value.find((item) => item.externalSku === sku)?.asin || ASIN_MISSING_PLACEHOLDER;
            const title =
                resellerItems.value.find((item) => item.externalSku === sku)?.title || ASIN_MISSING_PLACEHOLDER;
            return {
                url: asinToImageUrls.value?.get(asin) || DEFAULT_ITEM_IMAGE_URL,
                title: title,
            };
        },
        [asinToImageUrls.value, resellerItems.value, t]
    );

    const buildNewItem = () => {
        const item = {
            externalSku: '',
            itemId: '',
            quantity: 0,
            totalQuantity: 0,
            orderLineId: idRef.current,
            lineItemId: uuidv4(),
            complianceResolution: COMPLIANCE_RESOLUTION.NONE,
            returnReason: RETURN_REASON.NONE,
        };
        idRef.current += 1;
        return item;
    };

    function setOrderLines(orderLines: CreateReturnOrderLineItem[]) {
        if (returnOrderDraft.value) {
            const tempReturnOrder = {
                ...returnOrderDraft.value,
                orderLines: orderLines,
            };
            setReturnOrderDraft(tempReturnOrder);
            setIsSubmitEnabled(returnOrderValid(tempReturnOrder));
        }
    }

    const setReferenceId = (referenceId: string) => {
        if (returnOrderDraft.value) {
            if (referenceId.length > 30 || !customerRefRegEx.test(referenceId) || referenceId.length === 0) {
                setShowFormErrors(true);
            }

            const tempReturnOrder = {
                ...returnOrderDraft.value,
                referenceId: referenceId,
            };
            setReturnOrderDraft(tempReturnOrder);
            setIsSubmitEnabled(returnOrderValid(tempReturnOrder));
        }
    };

    const fillTotalQuantity = (orderLine: CreateReturnOrderLineItem, returnType: ReturnOrderType = 'RETURN') => {
        const modifiedLine = orderLine;
        const priceListItems = resellerPriceList.value?.priceListItems;
        if (returnType === 'RETURN') {
            modifiedLine.totalQuantity = modifiedLine.quantity;
        } else if (!orderLine.packaging || !orderLine.externalSku) {
            modifiedLine.totalQuantity = undefined;
        } else {
            const matchedItem = externalSkuToItemMap.get(orderLine.externalSku);
            if (matchedItem) {
                const packageQuantity = getUnitsOnPackage(matchedItem, orderLine.packaging) ?? 0;
                modifiedLine.totalQuantity = orderLine.quantity * packageQuantity;
                if (priceListItems) {
                    modifiedLine.estimatedCredit = (
                        (priceListItems.find((item) => item.itemId === orderLine.itemId)?.basePrice ?? 0) *
                        (orderLine.quantity * packageQuantity ?? 0)
                    ).toString();
                }
            } else {
                modifiedLine.totalQuantity = undefined;
            }
        }
        return modifiedLine;
    };

    const setReturnType = (returnType: string) => {
        if (returnOrderDraft.value) {
            const tempReturnOrder = {
                ...returnOrderDraft.value,
                orderLines: returnOrderDraft.value.orderLines.map((orderLine: CreateReturnOrderLineItem) => {
                    const modifiedOrderLine: CreateReturnOrderLineItem = {
                        itemId: orderLine.itemId,
                        orderLineId: orderLine.orderLineId,
                        quantity: orderLine.quantity,
                        packaging: orderLine.packaging,
                        parentOrderLineId: orderLine.parentOrderLineId,
                        lineItemId: orderLine.lineItemId,
                        externalSku: orderLine.externalSku,
                        returnReason: orderLine.returnReason,
                    };
                    return fillTotalQuantity(modifiedOrderLine, returnType);
                }),
                returnType: returnType,
            };
            setReturnOrderDraft(tempReturnOrder);
            setIsSubmitEnabled(returnOrderValid(tempReturnOrder));
        }
    };

    const previousReturnType = useRef<ReturnOrderType>(RETURN_TYPE.RETURN);
    useEffect(() => {
        if (
            returnOrderDraft.value?.returnType !== previousReturnType.current &&
            returnOrderDraft.value?.returnType === RETURN_TYPE.RETURN
        ) {
            calculateCompliance(returnOrderDraft);
        }
        previousReturnType.current = returnOrderDraft.value?.returnType ?? RETURN_TYPE.RETURN;
    }, [previousReturnType, returnOrderDraft, calculateCompliance]);

    const setShippingAddressCode = (shippingAddressLocationCode: string) => {
        if (returnOrderDraft.value) {
            const tempReturnOrder = {
                ...returnOrderDraft.value,
                shippingLocationCode: shippingAddressLocationCode,
            };
            setReturnOrderDraft(tempReturnOrder);
        }
    };

    const setBillingAddressCode = (billingAddressLocationCode: string) => {
        if (returnOrderDraft.value) {
            const tempReturnOrder = {
                ...returnOrderDraft.value,
                billingLocationCode: billingAddressLocationCode,
            };
            setReturnOrderDraft(tempReturnOrder);
        }
    };

    const getUnitsOnPackage = (
        item: ResellerItem | undefined,
        packaging: PackageType | undefined
    ): number | undefined => {
        if (item) {
            switch (packaging) {
                case 'CASE':
                    return item.unitsPerCase ?? 0;
                case 'PALLET':
                    return item.unitsOnPallet ?? 0;
                default:
                    return 1;
            }
        }
        return undefined;
    };

    const setContactEmailAddresses = (contactEmailAddresses: string[]) => {
        if (returnOrderDraft.value) {
            const tempReturnOrder = {
                ...returnOrderDraft.value,
                contactEmailAddresses: contactEmailAddresses,
            };
            setReturnOrderDraft(tempReturnOrder);
        }
    };

    const deleteAttachmentsOnSubmit = async () => {
        const attachmentsToDelete = attachments
            .value!.filter(
                (attachment: Attachment) =>
                    returnOrderId &&
                    attachment.type === 'ORDER_LINE_IMAGE_UPLOAD' &&
                    (returnOrderDraft.value!.returnType === RETURN_TYPE.RETURN || attachment.status === 'Deleted')
            )
            .map((attachment: any) => {
                return {
                    id: attachment.id,
                    versionNumber: attachment.version,
                    attachmentName: attachment.file.name as string,
                } as DeleteAttachmentRequest;
            });

        if (attachmentsToDelete.length > 0) {
            await deleteAttachments({
                returnOrderId: returnOrderId,
                resellerId: resellerId,
                requests: attachmentsToDelete,
            } as DeleteAttachmentsInput)
                .then((response) => {
                    if (response.deletedAttachments.length !== attachmentsToDelete.length) {
                        const deletedAttachmentIds = response.deletedAttachments.map((attachment) => attachment.id);
                        const failedAttachmentsString = attachmentsToDelete
                            .filter((attachment) => !deletedAttachmentIds.includes(attachment.id))
                            .map((attachment) => attachment.attachmentName)
                            .join(', ');
                        showError(
                            t('deleteAttachmentsList-failureMessage', {
                                attachmentList: failedAttachmentsString,
                            })
                        );
                        throw new Error('Failed to delete attachments.');
                    } else {
                        showSuccess(t('deleteAttachments', { context: 'success' }));
                        setAttachments(attachments.value!.filter((attachment) => attachment.status !== 'Deleted'));
                    }
                })
                .catch((e) => {
                    Logger.error(e);
                    showError(t('deleteAttachments', { context: 'failure' }));
                    throw new Error('Failed to delete attachments.');
                });
        }
    };

    const submitReturnOrderDraft = async () => {
        let returnOrderDraftIdForImageUpload;
        if (returnOrderDraft.value) {
            try {
                const createReturnOrderDraftLines: CreateReturnOrderDraftLine[] = [];
                returnOrderDraft.value.orderLines.forEach((line) => {
                    if (line.externalSku) {
                        const matchedItem = resellerItems.value?.find((item) => item.externalSku === line.externalSku);
                        createReturnOrderDraftLines.push({
                            itemId: matchedItem!.itemId,
                            resellerSku: matchedItem!.externalSku,
                            requestedQuantity: parseInt((line.totalQuantity as unknown) as string),
                            complianceResolution: line.complianceResolution
                                ? (line.complianceResolution as COMPLIANCE_RESOLUTION)
                                : COMPLIANCE_RESOLUTION.NONE,
                            packageType:
                                returnOrderDraft.value!.returnType === RETURN_TYPE.RETURN ? 'EACH' : line.packaging,
                            packageQuantity: parseInt((line.quantity as unknown) as string),
                            orderLineItemId: line.lineItemId,
                            returnReason: getReturnReason(line),
                        });
                    }
                });
                if (returnOrderId) {
                    const updateReturnOrderDraftOutput = await updateReturnOrderDraft({
                        returnOrderIdAndVersion: {
                            returnOrderDraftId: returnOrderDraft.value!.returnOrderDraftId!,
                            version: returnOrderDraft.value!.version!,
                        },
                        returnOrderDraft: {
                            resellerId: resellerId,
                            customerReferenceId: returnOrderDraft.value!.referenceId,
                            shippingAddress: returnOrderDraft.value!.shippingLocationCode,
                            billingAddress: returnOrderDraft.value!.billingLocationCode,
                            contactEmails: returnOrderDraft.value!.contactEmailAddresses,
                            returnOrderLines: createReturnOrderDraftLines,
                            returnType: returnOrderDraft.value!.returnType,
                        },
                    });
                    setReturnOrderDraft({
                        ...returnOrderDraft.value!,
                        orderLines: returnOrderDraft.value!.orderLines.filter(
                            (orderLine) => orderLine.externalSku && orderLine.itemId
                        ),
                        returnOrderDraftId: updateReturnOrderDraftOutput.returnOrderId,
                        version: updateReturnOrderDraftOutput.version,
                    });
                    returnOrderDraftIdForImageUpload = updateReturnOrderDraftOutput.returnOrderId;
                } else {
                    const createReturnOrderDraftOutput = await createReturnOrderDraft({
                        returnOrderDraft: {
                            resellerId: resellerId,
                            customerReferenceId: returnOrderDraft.value!.referenceId,
                            shippingAddress: returnOrderDraft.value!.shippingLocationCode,
                            billingAddress: returnOrderDraft.value!.billingLocationCode,
                            contactEmails: returnOrderDraft.value!.contactEmailAddresses,
                            returnOrderLines: createReturnOrderDraftLines,
                            returnType: returnOrderDraft.value!.returnType,
                        },
                    });
                    setReturnOrderDraft({
                        ...returnOrderDraft.value!,
                        orderLines: returnOrderDraft.value!.orderLines.filter(
                            (orderLine) => orderLine.externalSku && orderLine.itemId
                        ),
                        returnOrderDraftId: createReturnOrderDraftOutput.returnOrderId,
                        version: createReturnOrderDraftOutput.version,
                    });
                    returnOrderDraftIdForImageUpload = createReturnOrderDraftOutput.returnOrderId;
                }
                showSuccess(t('createdReturnOrderDraft-successMessage'));
                clearReturnOrderList();
                return returnOrderDraftIdForImageUpload;
            } catch (e) {
                Logger.error(e);
                switch (e.code) {
                    case 'CustomerReferenceIdAlreadyInUse':
                        showError(t('referenceIdAlreadyInUse-errorMessage'));
                        break;
                    default:
                        showError(t('failedToCreateReturnOrderDraft-errorMessage'));
                        break;
                }
                throw new Error('Failed to create/update return order draft');
            } finally {
                setShowFormErrors(false);
            }
        }
        return returnOrderDraftIdForImageUpload;
    };

    const uploadImagesOnSubmit = async (returnOrderId: string) => {
        if (
            returnOrderId &&
            ((returnOrderDraft.value!.returnType as RETURN_TYPE) === RETURN_TYPE.BUYBACK ||
                (returnOrderDraft.value!.returnType as RETURN_TYPE) === RETURN_TYPE.REFUSAL)
        ) {
            const uploadAttachmentPromises = attachments
                .value!.filter((attachment) => attachment.status === 'New')
                .map((attachment: any) => {
                    if (attachment.status !== 'New') {
                        return attachment;
                    }
                    const modifiedAttachment: Attachment = { ...attachment };
                    const requestAttachmentUploadUrlInput: RequestAttachmentUploadUrlInput = {
                        returnOrderId: returnOrderId,
                        key2Name: 'OrderLineId',
                        key2Value: attachment.lineItemId,
                        filename: attachment.file.name,
                        type: 'ORDER_LINE_IMAGE_UPLOAD',
                        visibleToReseller: true,
                        mimeType: attachment.file.type,
                        description: `Order line id: ${attachment.lineItemId}`,
                    };
                    return requestAttachmentUploadUrl(requestAttachmentUploadUrlInput).then(
                        (response: RequestAttachmentUploadUrlOutput) => {
                            modifiedAttachment.id = response.id;
                            modifiedAttachment.status = 'Uploading';
                            PutClient.put(response.url, attachment.file);
                            return modifiedAttachment;
                        }
                    );
                });
            try {
                if (uploadAttachmentPromises.length > 0) {
                    const attachmentsInProgress = await Promise.all(uploadAttachmentPromises);
                    setAttachments(attachmentsInProgress);
                    setAttachmentsStatus('Uploading');
                    showSuccess(t('orderLineImageUpload', { context: 'success' }));
                }
            } catch (e) {
                Logger.error(e);
                showError(t('orderLineImageUpload', { context: 'failure' }));
                throw new Error('Failed to upload order line images.');
            }
        }
    };

    async function deleteDraft() {
        try {
            await deleteReturnOrderDraft({
                returnOrderIdAndVersion: {
                    returnOrderDraftId: returnOrderDraft.value!.returnOrderDraftId!,
                    version: returnOrderDraft.value!.version!,
                },
                resellerId: resellerId,
            });
            clearReturnOrderList();
            clearReturnOrderDraft();
            showSuccess(t('returnOrderDeleted-successMessage'));
        } catch (e) {
            Logger.error(e);
            showError(t('returnOrderDeleted-errorMessage'));
            throw new Error('Failed to delete return order');
        }
    }

    async function submitForm(event: FormEvent) {
        event.preventDefault();

        if (!formIsValid) {
            setShowFormErrors(true);
            if (formErrors.orderLines) {
                Logger.debug(formErrors.orderLines);
                showError(formErrors.orderLines);
            }
            return;
        }

        try {
            setIsPageBusy(true);
            if (returnOrderDraft.value) {
                await deleteAttachmentsOnSubmit();
                const returnOrderDraftIdForImageUpload = await submitReturnOrderDraft();
                if (returnOrderDraftIdForImageUpload) {
                    await uploadImagesOnSubmit(returnOrderDraftIdForImageUpload);
                }
                goto(SiteMapPage.confirmReturnOrder);
            }
        } catch (e) {
            Logger.error(e);
            showError(t('submitForm', { context: 'failure' }));
        } finally {
            setIsPageBusy(false);
        }
    }

    const handleRemoveAttachments = (lines: CreateReturnOrderLineItem[], indexToRemove: number): Attachment[] => {
        const updatedAttachments = attachments.value!.filter(
            (attachment: Attachment) =>
                attachment.lineItemId !== lines[indexToRemove].lineItemId || attachment.status !== 'New'
        );
        return updatedAttachments.map((attachment: Attachment) => {
            if (attachment.lineItemId === lines[indexToRemove].lineItemId && attachment.status === 'Loaded') {
                return {
                    ...attachment,
                    status: 'Deleted',
                } as Attachment;
            } else {
                return attachment;
            }
        });
    };

    const quantityChanged = useRef(false);
    const calculateComplianceDebounced = useCallback(
        _.debounce((returnOrderDraft: IAsynchronouslyLoadedValue<ReturnOrderDraft>) => {
            calculateCompliance(returnOrderDraft);
        }, 1000),
        [calculateCompliance]
    );

    useEffect(() => {
        // We calculate the order lines' compliance the first time the page loads if return is a draft,
        // and we already have the sku of each order line.
        if (
            !isComplianceMeasured &&
            returnOrderDraft.value?.returnOrderDraftId &&
            returnOrderDraft.value?.returnType === RETURN_TYPE.RETURN &&
            getValidLineItems(returnOrderDraft.value?.orderLines).length > 0
        ) {
            setIsComplianceMeasured(true);
            calculateCompliance(returnOrderDraft);
        }
    }, [calculateCompliance, getValidLineItems, isComplianceMeasured, returnOrderDraft, returnOrderDraft.value]);

    useEffect(() => {
        if (quantityChanged.current && returnOrderDraft.value?.returnType === RETURN_TYPE.RETURN) {
            calculateComplianceDebounced(returnOrderDraft);
            quantityChanged.current = false;
        }
        setItemsNeedingActionNumber(
            returnOrderDraft.value?.orderLines.filter((item) => lineItemNeedsAction(item)).length || 0
        );
        setSelectedItemsNumber(returnOrderDraft.value?.orderLines.filter((item) => Boolean(item.selected)).length || 0);
    }, [calculateComplianceDebounced, returnOrderDraft]);

    const onOrderLinesChange = (lines: CreateReturnOrderLineItem[], changedIndex?: number, fieldName?: string) => {
        let modifiedOrderLines = lines;

        if (changedIndex !== undefined && fieldName === 'returnReason') {
            const changedLine = lines[changedIndex];
            if (changedLine.returnReason !== RETURN_REASON.OTHER && changedLine.customReason) {
                modifiedOrderLines[changedIndex] = { ...changedLine, customReason: '' };
            }
        }

        if (
            changedIndex !== undefined &&
            (fieldName === 'externalSku' || fieldName === 'asin' || fieldName === 'item')
        ) {
            modifiedOrderLines = fillItemIdentifiers(lines, changedIndex);
            setOrderLines(modifiedOrderLines);
            setAttachments(handleRemoveAttachments(lines, changedIndex));
        } else if (changedIndex !== undefined && fieldName === 'label') {
            setOrderLines(handleActionChange(lines, changedIndex));
        } else if (changedIndex !== undefined && (fieldName === 'quantity' || fieldName === 'packaging')) {
            modifiedOrderLines[changedIndex] = fillTotalQuantity(
                modifiedOrderLines[changedIndex],
                returnOrderDraft.value?.returnType
            );

            setOrderLines(modifiedOrderLines);
            quantityChanged.current = true;
        } else {
            setOrderLines(lines);
        }

        if (lines.length === 0 || lines[lines.length - 1].externalSku !== '') {
            setOrderLines([...modifiedOrderLines, buildNewItem()]);
        }
    };

    const deleteOrderLine = (lines: CreateReturnOrderLineItem[], indexToRemove: number) => {
        setOrderLines(lines.filter((_, i) => i !== indexToRemove));

        setAttachments(handleRemoveAttachments(lines, indexToRemove));
    };

    const fillItemIdentifiers = (lines: CreateReturnOrderLineItem[], changedIndex: number) => {
        const changedLine = lines[changedIndex];
        return lines.map((line) => {
            if (line === changedLine) {
                const matchedItem = resellerItems.value?.find((i) => i.externalSku === line.externalSku);
                if (matchedItem) {
                    setIsSubmitEnabled(false);
                    return {
                        ...buildNewItem(),
                        orderLineId: line.orderLineId,
                        itemId: matchedItem.itemId,
                        externalSku: matchedItem.externalSku,
                    };
                }
            }
            return line;
        });
    };

    const handleActionChange = (lines: CreateReturnOrderLineItem[], changedIndex: number) => {
        const changedLine = lines[changedIndex];
        const originalQuantity = changedLine.quantity;
        const newAction = lines[changedIndex].label;
        const modifiedOrderLines = lines;
        if (newAction === COMPLIANCE_RESOLUTION.ACCEPT) {
            modifiedOrderLines[changedIndex].complianceResolution = newAction;
        } else if (changedLine.approvedCount! > 0) {
            // if the approved count is greater than zero update initial line and create child line as appropriate
            modifiedOrderLines[changedIndex].quantity = changedLine.approvedCount!;
            modifiedOrderLines[changedIndex].complianceResolution = undefined;
            modifiedOrderLines[changedIndex].complianceResult = undefined;
            modifiedOrderLines[changedIndex] = fillTotalQuantity(
                modifiedOrderLines[changedIndex],
                returnOrderDraft.value?.returnType
            );

            if (newAction === COMPLIANCE_RESOLUTION.DISPOSE) {
                const newItem: CreateReturnOrderLineItem = {
                    ...buildNewItem(),
                    orderLineId: modifiedOrderLines[changedIndex].orderLineId + 0.001,
                    externalSku: changedLine.externalSku,
                    approvedCount: 0,
                    estimatedCredit: '',
                    quantity: originalQuantity - changedLine.approvedCount!,
                    complianceResolution: newAction,
                    complianceCalculationStatus: 'Complete' as ComplianceCalculationStatus,
                    parentOrderLineId: changedLine.orderLineId,
                };
                modifiedOrderLines.push(fillTotalQuantity(newItem, returnOrderDraft.value?.returnType));
            }
        } else {
            // Update the existing line based on the action
            modifiedOrderLines[changedIndex].complianceResolution = newAction;
            modifiedOrderLines[changedIndex].complianceCalculationStatus = 'Complete' as ComplianceCalculationStatus;
        }

        modifiedOrderLines[changedIndex].selected = false;

        return sortedOrderLines(modifiedOrderLines);
    };

    const currencyFormat = (options: { isLoading?: (item: any) => boolean }): MultilineFormEditorColumnFormat => ({
        render: (item, column) => {
            if (options.isLoading) {
                if (options.isLoading(item)) {
                    return (
                        <Column alignmentHorizontal={'center'}>
                            <Loader size={'small'} />
                        </Column>
                    );
                }
            }

            let value = t('priceList-loading');
            if (resellerPriceList.value) {
                value = i18next.t('complianceResolutionEstimatedCredit', {
                    ns: 'createReturnOrderPage',
                    value: new Intl.NumberFormat(i18next.language, {
                        style: 'currency',
                        currency: resellerPriceList.value.currencyCode,
                    }).format(item[column.sourceProperty] >= 0 ? item[column.sourceProperty] : 0),
                });
            }

            return <Text alignment={'left'}>{value}</Text>;
        },
    });

    const lineItemActionSortFunction = (a: CreateReturnOrderLineItem, b: CreateReturnOrderLineItem): number => {
        if (!a.externalSku || !b.externalSku) return 0;
        // Sorting strategy:
        // 1. Actionable/actioned items and their children first
        // 2. Within actionable groups: group by parent, then parent before child
        // 3. Non-actionable items last, sorted by orderLineId

        // Helper functions
        const lineItemWasActioned = (item: CreateReturnOrderLineItem): boolean => item.label !== undefined;

        const isActionableOrActioned = (item: CreateReturnOrderLineItem): boolean =>
            lineItemNeedsAction(item) || lineItemWasActioned(item);

        const isChild = (item: CreateReturnOrderLineItem): boolean =>
            !Number.isInteger(item.orderLineId) && item.parentOrderLineId != null;

        const isHighPriority = (item: CreateReturnOrderLineItem): boolean =>
            isActionableOrActioned(item) || isChild(item);

        const getGroupKey = (item: CreateReturnOrderLineItem): number =>
            isChild(item) ? item.parentOrderLineId! : item.orderLineId;

        // Check priority first
        const aIsHighPriority = isHighPriority(a);
        const bIsHighPriority = isHighPriority(b);

        if (aIsHighPriority !== bIsHighPriority) {
            return aIsHighPriority ? -1 : 1;
        }

        if (aIsHighPriority) {
            // Both are high priority - sort by group, then position within group
            const groupA = getGroupKey(a);
            const groupB = getGroupKey(b);

            if (groupA !== groupB) {
                return groupA - groupB;
            }

            // Same group - parents before children
            const aIsChild = isChild(a);
            const bIsChild = isChild(b);

            if (aIsChild !== bIsChild) {
                return aIsChild ? 1 : -1;
            }

            // Same position in group - sort by orderLineId
            return a.orderLineId - b.orderLineId;
        }

        return a.orderLineId - b.orderLineId;
    };
    const packageTypeSortFunction = (a: CreateReturnOrderLineItem, b: CreateReturnOrderLineItem): number => {
        if (!a.externalSku || !b.externalSku) return 0;
        const indexA = packageTypes.indexOf(a.packaging as string);
        const indexB = packageTypes.indexOf(b.packaging as string);
        return indexA - indexB;
    };

    const itemSortFunction = (
        a: CreateReturnOrderLineItem,
        b: CreateReturnOrderLineItem,
        field: keyof ResellerItem
    ): number => {
        if (!a.externalSku || !b.externalSku) return 0;
        const itemA = resellerItems.value?.find((item) => item.externalSku === a.externalSku);
        const itemB = resellerItems.value?.find((item) => item.externalSku === b.externalSku);
        const valueA = String(itemA?.[field] ?? '');
        const valueB = String(itemB?.[field] ?? '');
        return valueA.localeCompare(valueB);
    };

    const numberSortFunction = (
        a: CreateReturnOrderLineItem,
        b: CreateReturnOrderLineItem,
        column: keyof CreateReturnOrderLineItem
    ): number => {
        const numA = a[column as keyof CreateReturnOrderLineItem] as number;
        const numB = b[column as keyof CreateReturnOrderLineItem] as number;

        if (!a.externalSku || !b.externalSku) {
            return 0;
        }

        return numA - numB;
    };

    const lineItemNeedsAction = (item: CreateReturnOrderLineItem): boolean => {
        return !(
            item.complianceCalculationStatus !== 'Complete' ||
            item.eligibility === undefined ||
            item.quantity <= item.eligibility
        );
    };

    const sharedReturnOrderColumns: MultilineFormEditorColumn[] = [
        {
            title: t('orderLineId-columnTitle'),
            sourceProperty: 'orderLineId',
            isKey: true,
            isVisible: false,
        },
        {
            columnHeader: (
                <Checkbox
                    checked={selectedItemsNumber === itemsNeedingActionNumber}
                    onChange={(newState: boolean) => {
                        if (returnOrderDraft.value) {
                            const updatedLines = returnOrderDraft.value.orderLines.map((line) =>
                                lineItemNeedsAction(line) ? { ...line, selected: newState } : line
                            );
                            onOrderLinesChange(updatedLines);
                        }
                    }}
                />
            ),
            title: t('selectLineItem-checkbox'),
            sourceProperty: 'selected',
            isVisible: itemsNeedingActionNumber > 0,
            format: checkboxFormat({
                isDisabled: (item) => !lineItemNeedsAction(item),
            }),
        },
        {
            title: t('sku-columnTitle'),
            sourceProperty: 'externalSku',
            width: 'fill',
            format: searchableSelectFormat({
                selectOptions: externalSkuOptions,
                placeholder: t('enterSku-placeholderText'),
                width: 150,
                valueField: 'externalSku',
                isDisabled: (item) => item.parentOrderLineId !== undefined,
                isVisible: (item) => item.parentOrderLineId === undefined,
            }),
            sortable: true,
            sortFunction: (a, b) => itemSortFunction(a, b, 'externalSku'),
        },
        {
            title: t('upc-columnTitle'),
            sourceProperty: 'upc',
            width: 'fill',
            format: searchableSelectFormat({
                selectOptions: upcOptions,
                placeholder: t('enterUpc-placeholderText'),
                width: 150,
                valueField: 'externalSku',
                isDisabled: (item) => item.parentOrderLineId !== undefined,
                isVisible: (item) => item.parentOrderLineId === undefined,
            }),
            sortable: true,
            sortFunction: (a, b) => itemSortFunction(a, b, 'upc'),
        },
        {
            title: t('asin-columnTitle'),
            sourceProperty: 'asin',
            width: 'fill',
            format: searchableSelectFormat({
                selectOptions: asinOptions,
                placeholder: t('enterAsin-placeholderText'),
                width: 150,
                valueField: 'externalSku',
                isDisabled: (item) => item.parentOrderLineId !== undefined,
            }),
            sortable: true,
            sortFunction: (a, b) => itemSortFunction(a, b, 'asin'),
        },
        {
            title: t('item-columnTitle'),
            sourceProperty: 'item',
            width: 'fill',
            format: searchableSelectFormat({
                selectOptions: itemOptions,
                placeholder: t('enterItemName-placeholderText'),
                width: 150,
                valueField: 'externalSku',
                isDisabled: (item) => item.parentOrderLineId !== undefined,
            }),
            sortable: true,
            sortFunction: (a, b) => itemSortFunction(a, b, 'title'),
        },
        {
            title: t.cognate(' '),
            sourceProperty: 'externalSku',
            width: 'fill',
            format: imagePopupFormat(getItemImageUrlAndDataFromStore),
        },
    ];

    const standardReturnOrderColumns: MultilineFormEditorColumn[] = [
        {
            title: t('quantity-columnTitle'),
            sourceProperty: 'quantity',
            width: 100,
            format: numberFormat({ label: t('quantity-columnTitle') }),
            sortable: true,
            sortFunction: (a, b) => numberSortFunction(a, b, 'quantity'),
        },
        {
            title: t('approved-columnTitle'),
            sourceProperty: 'approvedCount',
            width: 100,
            format: textFormat({
                isLoading: (item) => item.complianceCalculationStatus === 'Loading',
            }),
            sortable: true,
            sortFunction: (a, b) => numberSortFunction(a, b, 'approvedCount'),
        },
        {
            title: t('estimatedCredit-columnTitle'),
            sourceProperty: 'estimatedCredit',
            width: 'fill',
            format: currencyFormat({
                isLoading: (item) => item.complianceCalculationStatus === 'Loading',
            }),
            sortable: true,
            sortFunction: (a, b) => numberSortFunction(a, b, 'estimatedCredit'),
        },
        {
            title: t('reason-columnTitle'),
            sourceProperty: 'reason',
            width: 200,
            format: createReturnOrderPageReturnReasonFormat({
                isLoading: (item) => item.complianceCalculationStatus === 'Loading',
                getReasonText: (reasonValue) => {
                    if (reasonValue === COMPLIANCE_RESOLUTION.NONE) return t.cognate('');
                    return t('complianceResolutionReason', { context: reasonValue });
                },
                getComplianceReasonExplainer: (reasonValue) => {
                    if (reasonValue === COMPLIANCE_RESOLUTION.NONE) return t.cognate('');
                    return t('complianceResolutionExplanation', { context: reasonValue });
                },
                customReasonPlaceholder: t('customReason-inputPlaceholder'),
                returnReasonPlaceholder: t('returnReason-selectPlaceholder'),
                reasonOptions: returnReasons,
            }),
        },
        {
            title: t('label-columnTitle'),
            columnHeader: (
                <>
                    <Row minWidth={'10%'}>
                        <Text type={'h100'}>{t('label-columnTitle')}</Text>
                        <HelpTablePopover
                            header={t('contractResolutionActionHelpHeader')}
                            dataTestId={'actionHelpPopover'}
                            text={t('contractResolutionHelpDetails')}
                            tableRowItems={complianceHelpItems}
                        />
                    </Row>
                </>
            ),
            sourceProperty: 'label',
            width: 'fill',
            format: createReturnOrderPageLabelSelectFormat(complianceActions),
            sortable: true,
            sortFunction: lineItemActionSortFunction,
        },
    ];

    const buybackRefusalReturnOrderColumns: MultilineFormEditorColumn[] = [
        {
            title: t('quantity-columnTitle'),
            sourceProperty: 'quantity',
            width: '50',
            format: numberFormat({}),
            sortable: true,
            sortFunction: (a, b) => numberSortFunction(a, b, 'quantity'),
        },
        {
            title: t.expression('columnTitle', { context: 'packaging' }),
            sourceProperty: 'packaging',
            format: selectFormat({
                selectOptions: packageTypes.map((type) => {
                    return { value: type, label: t.expression('packagingType', { context: type }) };
                }),
                isDisabled: (line) => !line.externalSku,
                placeholder: t('placeholderText', { context: 'selectPackaging' }),
                filterOptions: (packagingOption, line) => {
                    const item = externalSkuToItemMap.get(line.externalSku);

                    if (item === undefined) {
                        return false;
                    }

                    switch (packagingOption.value) {
                        case 'CASE':
                            return !!item.unitsPerCase;
                        case 'PALLET':
                            return !!item.unitsOnPallet;
                        default:
                            return true;
                    }
                },
            }),
            sortable: true,
            sortFunction: packageTypeSortFunction,
        },
        {
            title: t('columnTitle', { context: 'totalQuantity' }),
            sourceProperty: 'totalQuantity',
            width: 'fill',
            format: textFormat({
                isLoading: (item) => item.complianceCalculationStatus === 'Loading',
            }),
            sortable: true,
            sortFunction: (a, b) => numberSortFunction(a, b, 'totalQuantity'),
        },
        {
            title: t('estimatedCredit-columnTitle'),
            sourceProperty: 'estimatedCredit',
            width: 'fill',
            format: currencyFormat({
                isLoading: (item) => item.complianceCalculationStatus === 'Loading',
            }),
            sortable: true,
            sortFunction: (a, b) => numberSortFunction(a, b, 'estimatedCredit'),
        },
        {
            title: t.expression('columnTitle', { context: 'attachedImages' }),
            sourceProperty: 'imageUpload',
            width: 'fill',
            format: uploadImageFormat({
                attachments: attachments,
                setAttachments: setAttachments,
                disabled: attachments.status !== 'Loaded',
            }),
        },
    ];

    const orderLineColumns = useMemo(() => {
        if (
            returnOrderDraft.value &&
            [RETURN_TYPE.BUYBACK, RETURN_TYPE.REFUSAL].includes(returnOrderDraft.value.returnType as RETURN_TYPE)
        ) {
            return [...sharedReturnOrderColumns, ...buybackRefusalReturnOrderColumns];
        } else {
            return [...sharedReturnOrderColumns, ...standardReturnOrderColumns];
        }
    }, [
        returnOrderDraft.value,
        sharedReturnOrderColumns,
        standardReturnOrderColumns,
        buybackRefusalReturnOrderColumns,
    ]);

    if (resellerType === 'Indirect Retailer') {
        return null;
    }

    return (
        <form onSubmit={submitForm}>
            <Column spacing={'medium'}>
                <Tile title={t('createReturnOrder-pageTitle')} width={'100%'} contentSpacingInset={'small'}>
                    <WaitFor lazyValue={returnOrderDraft}>
                        {(returnOrderDraft) => (
                            <WaitFor lazyValue={resellerDetails}>
                                {(resellerDetails) => (
                                    <CreateReturnOrderInfoBox
                                        referenceId={returnOrderDraft.referenceId}
                                        setReferenceId={setReferenceId}
                                        returnOrderType={returnOrderDraft.returnType}
                                        setReturnOrderType={setReturnType}
                                        enabledReturnOrderTypes={supportedReturnTypes}
                                        locations={resellerDetails.resellerLocations.locations}
                                        shippingAddressLocationCode={returnOrderDraft.shippingLocationCode}
                                        setShippingAddressLocationCode={setShippingAddressCode}
                                        billingAddressLocationCode={returnOrderDraft.billingLocationCode}
                                        setBillingAddressLocationCode={setBillingAddressCode}
                                        contactEmails={returnOrderDraft.contactEmailAddresses}
                                        setContactEmails={setContactEmailAddresses}
                                        isAmazonAdminUser={isAmazonAdminUser}
                                        errors={showErrors()}
                                    />
                                )}
                            </WaitFor>
                        )}
                    </WaitFor>
                </Tile>
                <WaitFor lazyValue={returnOrderDraft}>
                    {(returnOrderDraft) => (
                        <WaitFor lazyValue={resellerItems}>
                            {() => (
                                <MultilineFormEditor
                                    title={t('enterItems-title')}
                                    columns={orderLineColumns}
                                    items={returnOrderDraft.orderLines}
                                    onItemChange={onOrderLinesChange}
                                    newItemBuilder={buildNewItem}
                                    maxHeight={350}
                                    onDeleteRowClick={deleteOrderLine}
                                    dataTestId={'createReturnOrderPage_MLFE'}
                                    sortable={true}
                                    defaultSortDirection={'ascending'}
                                    idField={'lineItemId'}
                                    defaultSortColumn={itemsNeedingActionNumber > 0 ? 'label' : undefined}
                                    tileHeader={
                                        itemsNeedingActionNumber > 0 && (
                                            <BulkActionTile
                                                handleActionChange={handleActionChange}
                                                setOrderLines={setOrderLines}
                                                complianceActions={complianceActions}
                                                selectedItemsNumber={selectedItemsNumber}
                                            />
                                        )
                                    }
                                />
                            )}
                        </WaitFor>
                    )}
                </WaitFor>
                <Row alignmentHorizontal={'right'}>
                    <Button
                        type={'secondary'}
                        onClick={() => {
                            clearReturnOrderDraft();
                            goto(SiteMapPage.listReturnOrders);
                        }}
                        data-testid={'cancelCreateReturnOrder-button'}
                    >
                        {t('cancelCreateReturnOrder-buttonLabel')}
                    </Button>
                    {returnOrderDraft.value?.returnOrderDraftId && (
                        <Button
                            type={'secondary'}
                            onClick={() => {
                                deleteDraft();
                                goto(SiteMapPage.listReturnOrders);
                            }}
                            data-testid={'deleteCreateReturnOrder-button'}
                            isBusy={isPageBusy}
                        >
                            {t('deleteCreateReturnOrder-buttonLabel')}
                        </Button>
                    )}
                    <Button
                        type={'primary'}
                        submit={true}
                        data-testid={'submitCreateReturnOrder-button'}
                        isBusy={isPageBusy}
                        disabled={!isSubmitEnabled}
                    >
                        {returnOrderDraft
                            ? t('updateCreateReturnOrder-buttonLabel')
                            : t('submitCreateReturnOrder-buttonLabel')}
                    </Button>
                </Row>
            </Column>
        </form>
    );
};

const mapStateToProps = ({
    resellerReducer,
    returnOrderDraftReducer,
    systemReducer,
    attachmentsReducer,
    userReducer,
}: RootState) => {
    return {
        resellerId: resellerReducer.selectedReseller.value!.resellerId,
        resellerDetails: resellerReducer.resellerDetails,
        resellerItems: resellerReducer.resellerItems,
        resellerPriceList: resellerReducer.resellerPriceList,
        returnOrderDraft: returnOrderDraftReducer.returnOrderDraft,
        asinToImageUrls: resellerReducer.asinToImageUrls,
        stage: systemReducer.stage.value,
        attachments: attachmentsReducer,
        zcrEnabled: resellerReducer.selectedReseller.value!.supportZeroCreditReturn,
        resellerType: resellerReducer.selectedReseller.value?.resellerType,
        adminUser: userReducer?.user,
        userEmail: userReducer?.user?.email || undefined,
    };
};

const mapDispatchToProps = {
    getResellerDetails,
    getResellerItems,
    getResellerPriceList,
    getReturnOrderDraft,
    setReturnOrderDraft,
    clearReturnOrderDraft,
    clearReturnOrderList,
    fetchAttachmentsByReturnId,
    clearAttachments,
    setAttachmentsStatus: setStatus,
    setAttachments,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
export type CreateReturnOrderPageProps = ConnectedProps<typeof connector>;
const connectedPage = connector(CreateReturnOrderPage);
export { connectedPage as CreateReturnOrderPage };
